import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const ACTION_COLORS = {
  "auth.login.success": "bg-emerald-50 text-emerald-700",
  "auth.login.failed": "bg-red-50 text-red-700",
  "pub.registered": "bg-blue-50 text-blue-700",
  "pub.status.active": "bg-emerald-50 text-emerald-700",
  "pub.status.paused": "bg-amber-50 text-amber-700",
  "pub.status.rejected": "bg-red-50 text-red-700",
  "pub.status.pending_approval": "bg-slate-100 text-slate-700",
};

export default function AdminAuditLog() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState({ actor: "", action: "", target_id: "" });
  const [appliedFilters, setAppliedFilters] = useState({ actor: "", action: "", target_id: "" });
  const LIMIT = 100;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: LIMIT, skip });
        Object.entries(appliedFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
        const { data: res } = await API.get(`/admin/audit-log?${params}`);
        setData(res.data);
        setTotal(res.total);
      } finally {
        setLoading(false);
      }
    })();
  }, [skip, appliedFilters]);

  const apply = () => { setSkip(0); setAppliedFilters(filters); };
  const reset = () => { setFilters({ actor: "", action: "", target_id: "" }); setAppliedFilters({ actor: "", action: "", target_id: "" }); setSkip(0); };

  return (
    <div className="p-8" data-testid="admin-audit-log">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Admin &middot; Security
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">
          Audit log
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Every sensitive mutation on this platform — who did it, when, and from where.
        </p>
      </div>

      {/* Filters */}
      <div className="border border-slate-200 rounded-md p-4 mb-6 grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Actor email</Label>
          <Input
            value={filters.actor}
            onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
            placeholder="admin@..."
            className="h-9 text-sm mt-1"
            data-testid="audit-filter-actor"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Action</Label>
          <Input
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            placeholder="pub.status / auth.login"
            className="h-9 text-sm mt-1"
            data-testid="audit-filter-action"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Target ID</Label>
          <Input
            value={filters.target_id}
            onChange={(e) => setFilters({ ...filters, target_id: e.target.value })}
            placeholder="exact id"
            className="h-9 text-sm mt-1 font-mono"
            data-testid="audit-filter-target"
          />
        </div>
        <div className="flex items-end gap-2 sm:col-span-2">
          <Button onClick={apply} className="bg-[#002FA7] hover:bg-[#00227A] text-white h-9 flex-1" data-testid="audit-apply-btn">Apply</Button>
          <Button onClick={reset} variant="outline" className="h-9" data-testid="audit-reset-btn">Reset</Button>
        </div>
      </div>

      {/* Results */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            <span className="font-mono font-semibold text-slate-900">{total}</span> event{total === 1 ? "" : "s"}
            {skip > 0 && <span className="text-slate-400"> &middot; showing {skip + 1}–{Math.min(skip + LIMIT, total)}</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - LIMIT))}
              className="h-8 text-xs"
              data-testid="audit-prev-btn"
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={skip + LIMIT >= total}
              onClick={() => setSkip(skip + LIMIT)}
              className="h-8 text-xs"
              data-testid="audit-next-btn"
            >
              Next
            </Button>
          </div>
        </div>

        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">When</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">Actor</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">Action</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">Target</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-400">Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-400">No events match these filters.</td></tr>
              ) : (
                data.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-600 whitespace-nowrap">
                      {r.timestamp?.slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[13px] text-slate-900">{r.actor_email || <span className="text-slate-400">—</span>}</div>
                      <div className="text-[10px] font-mono text-slate-400">{r.actor_role || ""}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-mono font-semibold ${ACTION_COLORS[r.action] || "bg-slate-100 text-slate-700"}`}>
                        {r.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-700">
                      {r.target_label || <span className="text-slate-400">—</span>}
                      {r.target_id && <div className="text-[10px] font-mono text-slate-400">{r.target_id}</div>}
                    </td>
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-500">{r.ip || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
